import { ensureCsrfToken, requestJson as apiRequestJson, API_BASE_URL } from "../api/apiClient";

async function requestJson(path, options = {}) {
  return apiRequestJson(path, options);
}

async function requestFormData(path, formData, options = {}) {
  const { signal } = options;
  const csrfToken = await ensureCsrfToken();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    body: formData,
    credentials: "include",
    signal,
    headers: {
      "X-CSRF-Token": csrfToken,
    },
  });

  if (!response.ok) {
    let message = `Midpoint API request failed: ${response.status} ${response.statusText}`;

    try {
      const errorBody = await response.json();
      if (errorBody?.message) {
        message = errorBody.message;
      }
    } catch {
      // Keep the HTTP status message if the server did not return JSON.
    }

    throw new Error(message);
  }

  return response.json();
}

async function requestText(path, options = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: "include",
    ...options,
  });

  if (!response.ok) {
    throw new Error(`Midpoint API request failed: ${response.status} ${response.statusText}`);
  }

  return response.text();
}

function withArea(path, areaId) {
  if (!areaId) {
    return path;
  }

  return `${path}?areaId=${encodeURIComponent(areaId)}`;
}

function withQuery(path, params = {}) {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }

    if (Array.isArray(value)) {
      if (value.length) {
        search.set(key, value.join(","));
      }
      return;
    }

    search.set(key, String(value));
  });

  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

export async function getAreas() {
  return requestJson("/api/areas");
}

export async function getMapLayers() {
  return requestJson("/api/map-layers");
}

export async function fetchClientViews(options = {}) {
  return requestJson("/api/client-views", { signal: options.signal });
}

export async function getMapMarkers(areaId, options = {}) {
  return requestJson(
    withQuery("/api/markers", {
      areaId,
      layers: options.layers,
      sources: options.sources,
      source: options.source,
      type: options.types,
      bbox: options.bbox,
      limit: options.limit,
      transactionType: options.transactionType,
      listingStatus: options.listingStatus,
      propertyType: options.propertyType,
      minPrice: options.minPrice,
      maxPrice: options.maxPrice,
      minBedrooms: options.minBedrooms,
      minBathrooms: options.minBathrooms,
      minBuildingAreaSqM: options.minBuildingAreaSqM,
      maxBuildingAreaSqM: options.maxBuildingAreaSqM,
      clientViewId: options.clientViewId,
      clientViewSlug: options.clientViewSlug,
    }),
    { signal: options.signal }
  );
}

export async function getRealEstateListings(filters = {}, options = {}) {
  return requestJson(withQuery("/api/real-estate/listings", filters), {
    signal: options.signal,
  });
}

export async function getCanonicalRealEstateListings(filters = {}, options = {}) {
  return requestJson(withQuery("/api/real-estate/canonical-listings", filters), {
    signal: options.signal,
  });
}

export async function getCanonicalProperties(filters = {}, options = {}) {
  return requestJson(withQuery("/api/real-estate/canonical-properties", filters), {
    signal: options.signal,
  });
}

export async function getCanonicalPropertyAggregates(filters = {}, options = {}) {
  return requestJson(withQuery("/api/real-estate/canonical-properties/aggregates", filters), {
    signal: options.signal,
  });
}

export async function getGeoBoundaries(filters = {}, options = {}) {
  return requestJson(withQuery("/api/geo/boundaries", filters), { signal: options.signal });
}

export async function getGeoBoundary(boundaryId, options = {}) {
  return requestJson(`/api/geo/boundaries/${boundaryId}`, { signal: options.signal });
}

export async function getAdminGeoBoundarySummary(options = {}) {
  return requestJson("/api/admin/geo/boundaries/summary", { signal: options.signal });
}

export async function getAdminGeoBoundaries(filters = {}, options = {}) {
  return requestJson(withQuery("/api/admin/geo/boundaries", filters), {
    signal: options.signal,
  });
}

export async function importAdminGeoBoundaries(body, options = {}) {
  return requestJson("/api/admin/geo/boundaries/import", {
    method: "POST",
    body: JSON.stringify(body),
    signal: options.signal,
  });
}

export async function assignAdminListingBoundaries(body = {}, options = {}) {
  return requestJson("/api/admin/geo/boundaries/assign-listings", {
    method: "POST",
    body: JSON.stringify(body),
    signal: options.signal,
  });
}

export async function patchAdminGeoBoundary(boundaryId, body, options = {}) {
  return requestJson(`/api/admin/geo/boundaries/${boundaryId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
    signal: options.signal,
  });
}

export async function createAdminMarketZone(body, options = {}) {
  return requestJson("/api/admin/geo/market-zones", {
    method: "POST",
    body: JSON.stringify(body),
    signal: options.signal,
  });
}

export async function searchMidpoint(filters = {}, options = {}) {
  const query = {
    ...filters,
    q: filters.q || filters.search || undefined,
    search: undefined,
  };

  return requestJson(withQuery("/api/search", query), { signal: options.signal });
}

export async function searchLocations(filters = {}, options = {}) {
  return requestJson(withQuery("/api/locations/search", filters), { signal: options.signal });
}

/**
 * Viewport canonical property search for the Real Estate perspective.
 * Aborts prior in-flight request when a new AbortSignal is supplied by the caller.
 */
export async function fetchCanonicalProperties(
  { bbox, zoom, limit, offset, sort, signal, ...filters } = {}
) {
  return getCanonicalProperties(
    {
      ...filters,
      bbox,
      zoom,
      limit,
      offset,
      sort,
    },
    { signal }
  );
}

export async function fetchCanonicalPropertyAggregates(
  { bbox, zoom, signal, ...filters } = {}
) {
  return getCanonicalPropertyAggregates(
    {
      ...filters,
      bbox,
      zoom,
    },
    { signal }
  );
}

export async function getCanonicalRealEstateSummary(filters = {}, options = {}) {
  return requestJson(withQuery("/api/real-estate/canonical-summary", filters), {
    signal: options.signal,
  });
}

export async function getRealEstateMarkers(filters = {}) {
  return getMapMarkers(undefined, {
    layers: "realEstate",
    ...filters,
  });
}

export async function getRealEstateByViewport({ bbox, limit, signal, ...filters } = {}) {
  const queryFilters = { bbox, limit, ...filters };
  const [listingsResponse, summary] = await Promise.all([
    getCanonicalProperties(queryFilters, { signal }).catch(() =>
      getCanonicalRealEstateListings(queryFilters, { signal })
    ),
    getCanonicalRealEstateSummary(queryFilters, { signal }).catch(() => null),
  ]);

  const properties = Array.isArray(listingsResponse?.properties)
    ? listingsResponse.properties
    : Array.isArray(listingsResponse?.data)
      ? listingsResponse.data
      : Array.isArray(listingsResponse)
        ? listingsResponse
        : [];

  return {
    properties,
    summary,
    count: listingsResponse?.total ?? listingsResponse?.count ?? properties.length,
    mode: "canonical",
  };
}

export async function getRealEstateMarkersByViewport({ areaId, bbox, limit, signal, ...filters } = {}) {
  return getMapMarkers(areaId, {
    layers: "real_estate",
    realEstateMode: "canonical",
    bbox,
    limit,
    signal,
    ...filters,
  });
}

export async function getRawRealEstateMarkersByViewport({ areaId, bbox, limit, signal, ...filters } = {}) {
  return getMapMarkers(areaId, {
    layers: "real_estate",
    realEstateMode: "raw",
    bbox,
    limit,
    signal,
    ...filters,
  });
}

export async function getInfrastructureMapMarkersByBbox({ bbox, areaId, types, limit, signal } = {}) {
  return getMapMarkers(areaId, {
    layers: "infrastructure",
    bbox,
    types,
    limit,
    signal,
  });
}

export async function getInfrastructureByViewport({ bbox, areaId, types, limit, signal } = {}) {
  return getInfrastructurePoints(areaId, {
    bbox,
    types,
    limit,
    signal,
  });
}

export async function getAreaSummary(areaId) {
  return requestJson(`/api/area-summary/${encodeURIComponent(areaId)}`);
}

export async function getAlerts(areaId) {
  return requestJson(withArea("/api/alerts", areaId));
}

export async function getWeatherSources(options = {}) {
  return requestJson(
    withQuery("/api/weather/sources", {
      providerType: options.providerType,
    }),
    { signal: options.signal }
  );
}

export async function getRadarFrames(options = {}) {
  return requestJson(
    withQuery("/api/weather/radar/frames", {
      sourceCode: options.sourceCode,
      productCode: options.productCode,
      refresh: options.refresh,
    }),
    { signal: options.signal }
  );
}

export async function getRadarProducts(options = {}) {
  return requestJson(
    withQuery("/api/weather/radar/products", {
      sourceCode: options.sourceCode,
      includeColorTable: options.includeColorTable,
    }),
    { signal: options.signal }
  );
}

export async function getRadarColorTables(options = {}) {
  return requestJson(
    withQuery("/api/weather/radar/color-tables", {
      includeStops: options.includeStops,
    }),
    { signal: options.signal }
  );
}

export async function getRadarColorTable(colorTableCode, options = {}) {
  const encodedCode = encodeURIComponent(colorTableCode);
  return requestJson(
    withQuery(`/api/weather/radar/color-tables/${encodedCode}`, {
      includeStops: options.includeStops,
    }),
    { signal: options.signal }
  );
}

export async function getWeatherProviderHealth(options = {}) {
  return requestJson(
    withQuery("/api/weather/provider-health", {
      limit: options.limit,
      sourceCode: options.sourceCode,
    }),
    { signal: options.signal }
  );
}

export async function getFloodRiskZones(options = {}) {
  return requestJson(
    withQuery("/api/weather/flood-risk-zones", {
      bbox: options.bbox,
      municipality: options.municipality,
      riskLevel: options.riskLevel,
      sourceCode: options.sourceCode,
      activeOnly: options.activeOnly,
    }),
    { signal: options.signal }
  );
}

export async function getHydrography(options = {}) {
  return requestJson(
    withQuery("/api/weather/hydrography", {
      bbox: options.bbox,
      municipality: options.municipality,
      waterwayType: options.waterwayType,
      sourceCode: options.sourceCode,
      activeOnly: options.activeOnly,
    }),
    { signal: options.signal }
  );
}

export async function getFloodContextSummary() {
  return requestJson("/api/weather/flood-context/summary");
}

export async function getLocalObservationSummary() {
  return requestJson("/api/weather/local-observations/summary");
}

export async function getWeatherStationsCurrent(options = {}) {
  return requestJson(
    withQuery("/api/weather/stations/current", {
      bbox: options.bbox,
      municipality: options.municipality,
      stationType: options.stationType,
      sourceCode: options.sourceCode,
      activeOnly: options.activeOnly,
    }),
    { signal: options.signal }
  );
}

export async function getHydrologyStationsCurrent(options = {}) {
  return requestJson(
    withQuery("/api/weather/hydrology/current", {
      bbox: options.bbox,
      municipality: options.municipality,
      stationType: options.stationType,
      alertLevel: options.alertLevel,
      sourceCode: options.sourceCode,
      activeOnly: options.activeOnly,
    }),
    { signal: options.signal }
  );
}

export async function getSiataDiscovery() {
  return requestJson("/api/weather/siata/discovery");
}

export async function refreshSampleLocalObservations() {
  return requestJson("/api/weather/local-observations/refresh-sample", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function getRainfallSummary(options = {}) {
  return requestJson("/api/weather/rainfall/summary", { signal: options.signal });
}

export async function getLatestRainfallSnapshots(options = {}) {
  return requestJson(
    withQuery("/api/weather/rainfall/snapshots/latest", {
      bbox: options.bbox,
      sourceCode: options.sourceCode,
      minRainfallMm: options.minRainfallMm,
      sampleOnly: options.sampleOnly,
    }),
    { signal: options.signal }
  );
}

export async function getNasaImergRainfallStatus(options = {}) {
  return requestJson("/api/weather/rainfall/nasa-imerg/status", { signal: options.signal });
}

export async function getLatestRainfallAccumulation(options = {}) {
  return requestJson(
    withQuery("/api/weather/rainfall/accumulation/latest", {
      bbox: options.bbox,
      sourceCode: options.sourceCode,
      window: options.window,
      sampleOnly: options.sampleOnly,
    }),
    { signal: options.signal }
  );
}

export async function getRainfallAreaSummary(options = {}) {
  return requestJson(
    withQuery("/api/weather/rainfall/area-summary", {
      bbox: options.bbox,
      sourceCode: options.sourceCode,
      window: options.window,
      areaType: options.areaType,
      municipality: options.municipality,
      sampleOnly: options.sampleOnly,
    }),
    { signal: options.signal }
  );
}

export async function refreshSampleRainfall() {
  return requestJson("/api/weather/rainfall/refresh-sample", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function getStormSummary(options = {}) {
  return requestJson(
    withQuery("/api/weather/storms/summary", { sampleOnly: options.sampleOnly }),
    { signal: options.signal }
  );
}

export async function getCurrentStormCells(options = {}) {
  return requestJson(
    withQuery("/api/weather/storm-cells/current", {
      bbox: options.bbox,
      sourceCode: options.sourceCode,
      intensity: options.intensity,
      sampleOnly: options.sampleOnly,
    }),
    { signal: options.signal }
  );
}

export async function getCurrentStormTracks(options = {}) {
  return requestJson(
    withQuery("/api/weather/storm-tracks/current", {
      bbox: options.bbox,
      sourceCode: options.sourceCode,
      sampleOnly: options.sampleOnly,
    }),
    { signal: options.signal }
  );
}

export async function getCurrentStormProjections(options = {}) {
  return requestJson(
    withQuery("/api/weather/storm-projections/current", {
      bbox: options.bbox,
      projectionMinutes: options.projectionMinutes,
      sourceCode: options.sourceCode,
      sampleOnly: options.sampleOnly,
    }),
    { signal: options.signal }
  );
}

export async function getStormImpactSummary(options = {}) {
  return requestJson(
    withQuery("/api/weather/storm-impact-summary", {
      stormCellId: options.stormCellId,
      projectionMinutes: options.projectionMinutes,
      sourceCode: options.sourceCode,
      sampleOnly: options.sampleOnly,
    }),
    { signal: options.signal }
  );
}

export async function detectSampleStorms() {
  return requestJson("/api/weather/storms/detect-sample", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function getOfficialAlertsSummary(options = {}) {
  return requestJson(
    withQuery("/api/weather/official-alerts/summary", {
      activeOnly: options.activeOnly,
      sampleOnly: options.sampleOnly,
      severity: options.severity,
      alertType: options.alertType,
    }),
    { signal: options.signal }
  );
}

export async function getOfficialAlertsGeoJson(options = {}) {
  return requestJson(
    withQuery("/api/weather/official-alerts/geojson", {
      bbox: options.bbox,
      activeOnly: options.activeOnly,
      sampleOnly: options.sampleOnly,
      severity: options.severity,
      alertType: options.alertType,
      status: options.status,
    }),
    { signal: options.signal }
  );
}

export async function refreshSampleOfficialAlerts() {
  return requestJson("/api/weather/official-alerts/refresh-sample", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function getCommunityReportsSummary(options = {}) {
  return requestJson(
    withQuery("/api/community/reports/summary", {
      verifiedOnly: options.verifiedOnly,
      sampleOnly: options.sampleOnly,
      reportType: options.reportType,
      status: options.status,
    }),
    { signal: options.signal }
  );
}

export async function getCommunityReportsGeoJson(options = {}) {
  return requestJson(
    withQuery("/api/community/reports/geojson", {
      bbox: options.bbox,
      verifiedOnly: options.verifiedOnly,
      includeRejected: options.includeRejected,
      sampleOnly: options.sampleOnly,
      reportType: options.reportType,
      status: options.status,
      severity: options.severity,
    }),
    { signal: options.signal }
  );
}

export async function submitMapCommunityReport(body) {
  return requestJson("/api/community/reports", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function verifyMapCommunityReport(reportId, body = {}) {
  return requestJson(`/api/community/reports/${encodeURIComponent(reportId)}/verify`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function rejectMapCommunityReport(reportId, body = {}) {
  return requestJson(`/api/community/reports/${encodeURIComponent(reportId)}/reject`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function resolveMapCommunityReport(reportId, body = {}) {
  return requestJson(`/api/community/reports/${encodeURIComponent(reportId)}/resolve`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function reviewMapCommunityReport(reportId, body = {}) {
  return requestJson(`/api/community/reports/${encodeURIComponent(reportId)}/review`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getMidpointWeatherAlertsGeoJson(options = {}) {
  return requestJson(
    withQuery("/api/weather/alerts/geojson", {
      bbox: options.bbox,
      activeOnly: options.activeOnly,
      sampleOnly: options.sampleOnly,
    }),
    { signal: options.signal }
  );
}

export async function getMidpointWeatherAlertsSummary(options = {}) {
  return requestJson(
    withQuery("/api/weather/alerts/summary", {
      activeOnly: options.activeOnly,
      sampleOnly: options.sampleOnly,
    }),
    { signal: options.signal }
  );
}

export async function refreshSampleMidpointAlerts() {
  return requestJson("/api/weather/alerts/refresh-sample", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function getAlertCorrelationsSummary(options = {}) {
  return requestJson(
    withQuery("/api/weather/alert-correlations/summary", {
      sampleOnly: options.sampleOnly,
    }),
    { signal: options.signal }
  );
}

export async function getAlertCorrelationsForMidpointAlert(weatherAlertId) {
  return requestJson(
    `/api/weather/alert-correlations/midpoint-alert/${encodeURIComponent(weatherAlertId)}`
  );
}

export async function runAlertCorrelations(body = { sampleOnly: true }) {
  return requestJson("/api/weather/alert-correlations/run", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getWeatherDashboardSummary(options = {}) {
  return requestJson(
    withQuery("/api/weather/dashboard/summary", {
      window: options.window,
      municipality: options.municipality,
      neighborhood: options.neighborhood,
      bbox: options.bbox,
      includeSample: options.includeSample,
      sampleOnly: options.sampleOnly,
      activeOnly: options.activeOnly,
    }),
    { signal: options.signal }
  );
}

export async function getWeatherDashboardTimeline(options = {}) {
  return requestJson(
    withQuery("/api/weather/dashboard/timeline", {
      window: options.window,
      municipality: options.municipality,
      neighborhood: options.neighborhood,
      bbox: options.bbox,
      includeSample: options.includeSample,
      sampleOnly: options.sampleOnly,
      eventTypes: options.eventTypes,
      severity: options.severity,
      limit: options.limit,
      offset: options.offset,
      sort: options.sort,
    }),
    { signal: options.signal }
  );
}

export async function getWeatherDashboardAreaSummary(options = {}) {
  return requestJson(
    withQuery("/api/weather/dashboard/area-summary", {
      window: options.window,
      municipality: options.municipality,
      neighborhood: options.neighborhood,
      bbox: options.bbox,
      floodRiskZoneId: options.floodRiskZoneId,
      weatherAlertId: options.weatherAlertId,
      stormCellId: options.stormCellId,
      includeSample: options.includeSample,
      sampleOnly: options.sampleOnly,
    }),
    { signal: options.signal }
  );
}

export async function getWeatherDashboardRiskRanking(options = {}) {
  return requestJson(
    withQuery("/api/weather/dashboard/risk-ranking", {
      window: options.window,
      municipality: options.municipality,
      bbox: options.bbox,
      includeSample: options.includeSample,
      sampleOnly: options.sampleOnly,
      limit: options.limit,
      areaType: options.areaType,
    }),
    { signal: options.signal }
  );
}

export async function getWeatherDashboardIncident(type, id) {
  const routes = {
    midpoint_alert: `/api/weather/dashboard/incident/midpoint-alert/${encodeURIComponent(id)}`,
    official_alert: `/api/weather/dashboard/incident/official-alert/${encodeURIComponent(id)}`,
    community_report: `/api/weather/dashboard/incident/community-report/${encodeURIComponent(id)}`,
    storm_cell: `/api/weather/dashboard/incident/storm-cell/${encodeURIComponent(id)}`,
  };
  return requestJson(routes[type]);
}

export function setMapFocusHint(focus) {
  sessionStorage.setItem(
    "midpointMapFocus",
    JSON.stringify({ ...focus, setAt: Date.now() })
  );
}

export async function getRealEstateSignals(areaId) {
  return requestJson(withArea("/api/real-estate-signals", areaId));
}

export async function getProperties(filters = {}, options = {}) {
  return requestJson(withQuery("/api/properties", filters), { signal: options.signal });
}

export async function getRealEstateSummary(filters = {}, options = {}) {
  return requestJson(withQuery("/api/real-estate-summary", filters), {
    signal: options.signal,
  });
}

export async function getPropertyById(id) {
  return requestJson(`/api/properties/${encodeURIComponent(id)}`);
}

export async function getInfrastructurePoints(areaId, options = {}) {
  return requestJson(
    withQuery("/api/infrastructure-points", {
      areaId,
      type: options.types,
      bbox: options.bbox,
      limit: options.limit,
    })
  );
}

export async function getInfrastructureSummary(areaId, options = {}) {
  return requestJson(
    withQuery("/api/infrastructure-summary", {
      areaId,
      type: options.types,
      bbox: options.bbox,
    })
  );
}

export async function getCommunityReports(areaId) {
  return requestJson(withArea("/api/community-reports", areaId));
}

export async function getSafetySignals(areaId) {
  return requestJson(withArea("/api/safety-signals", areaId));
}

export async function getIngestionRuns(limit) {
  return requestJson(withQuery("/api/ingestion-runs", { limit }));
}

export async function getIngestionHealth() {
  return requestJson("/api/ingestion-health");
}

export async function getAdminDataSources() {
  return requestJson("/api/admin/data-sources");
}

export async function getAdminDataHealth() {
  return requestJson("/api/admin/data-health");
}

export async function getAdminDataHealthBySource(sourceCode) {
  return requestJson(`/api/admin/data-health/source/${encodeURIComponent(sourceCode)}`);
}

export async function getRealEstateSources() {
  return requestJson("/api/admin/real-estate-sources");
}

export async function getRealEstatePipelineSources() {
  return requestJson("/api/admin/real-estate/sources");
}

export async function syncRealEstatePipelineSources() {
  return requestJson("/api/admin/real-estate/sources/sync", { method: "POST" });
}

export async function updateRealEstatePipelineSource(sourceKey, body) {
  return requestJson(`/api/admin/real-estate/sources/${encodeURIComponent(sourceKey)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function runRealEstatePipelineSource(sourceKey, options = {}) {
  return requestJson(
    `/api/admin/real-estate/sources/${encodeURIComponent(sourceKey)}/run`,
    {
      method: "POST",
      body: JSON.stringify(options),
    }
  );
}

export async function stopRealEstatePipelineSource(sourceKey, options = {}) {
  return requestJson(
    `/api/admin/real-estate/sources/${encodeURIComponent(sourceKey)}/stop`,
    {
      method: "POST",
      body: JSON.stringify(options),
    }
  );
}

export async function getRealEstatePipelineRuns(sourceKey, filters = {}) {
  return requestJson(
    withQuery(
      `/api/admin/real-estate/sources/${encodeURIComponent(sourceKey)}/runs`,
      filters
    )
  );
}

export async function getRealEstatePipelineRun(runId) {
  return requestJson(`/api/admin/real-estate/runs/${encodeURIComponent(runId)}`);
}

export const REAL_ESTATE_SCHEDULE_INTERVALS = [
  { label: "Manual only", minutes: null },
  { label: "Every 30 minutes", minutes: 30 },
  { label: "Every 1 hour", minutes: 60 },
  { label: "Every 3 hours", minutes: 180 },
  { label: "Every 6 hours", minutes: 360 },
  { label: "Every 12 hours", minutes: 720 },
  { label: "Daily", minutes: 1440 },
  { label: "Weekly", minutes: 10080 },
];

export const WEATHER_SCHEDULE_INTERVALS = [
  { label: "Manual only", minutes: null },
  { label: "Every 5 minutes", minutes: 5 },
  { label: "Every 15 minutes", minutes: 15 },
  { label: "Every 30 minutes", minutes: 30 },
  { label: "Every 1 hour", minutes: 60 },
  { label: "Every 3 hours", minutes: 180 },
  { label: "Every 6 hours", minutes: 360 },
  { label: "Every 12 hours", minutes: 720 },
  { label: "Daily", minutes: 1440 },
];

export async function getWeatherPipelineSources() {
  return requestJson("/api/admin/weather/sources");
}

export async function updateWeatherPipelineSource(sourceCode, body) {
  return requestJson(`/api/admin/weather/sources/${encodeURIComponent(sourceCode)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function runWeatherPipelineSource(sourceCode) {
  return requestJson(`/api/admin/weather/sources/${encodeURIComponent(sourceCode)}/run`, {
    method: "POST",
  });
}

export async function getWeatherPipelineHealth(sourceCode, filters = {}) {
  return requestJson(
    withQuery(
      `/api/admin/weather/sources/${encodeURIComponent(sourceCode)}/health`,
      filters
    )
  );
}

export async function getRealEstateReviewQueue(filters = {}) {
  return requestJson(withQuery("/api/admin/real-estate/review-queue", filters));
}

export async function resolveRealEstateReviewItem(reviewItemId, body) {
  return requestJson(
    `/api/admin/real-estate/review-queue/${encodeURIComponent(reviewItemId)}/resolve`,
    {
      method: "POST",
      body: JSON.stringify(body),
    }
  );
}

export async function getRealEstateSourceQuality(sourceCode, filters = {}) {
  return requestJson(
    withQuery(`/api/admin/real-estate-sources/${encodeURIComponent(sourceCode)}/quality`, filters)
  );
}

export async function getAdminSourceQuality() {
  return requestJson("/api/admin/real-estate/source-quality");
}

export async function getAdminSourceQualityDetail(sourceKey) {
  return requestJson(`/api/admin/real-estate/source-quality/${encodeURIComponent(sourceKey)}`);
}

export async function recalculateAllSourceQuality(body = {}) {
  return requestJson("/api/admin/real-estate/source-quality/recalculate", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getAdminImageHealth() {
  return requestJson("/api/admin/real-estate/image-health");
}

export async function getAdminImageHealthDetail(sourceKey, filters = {}) {
  return requestJson(
    withQuery(`/api/admin/real-estate/image-health/${encodeURIComponent(sourceKey)}`, filters)
  );
}

export async function validateAdminImageHealth(body = {}) {
  return requestJson("/api/admin/real-estate/image-health/validate", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function validateAdminImageRecord(imageId, body = {}) {
  return requestJson(`/api/admin/real-estate/image-health/${encodeURIComponent(imageId)}/validate`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function patchAdminImageHealth(imageId, body = {}) {
  return requestJson(`/api/admin/real-estate/image-health/${encodeURIComponent(imageId)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function getAdminMediaQualityDashboard() {
  return requestJson("/api/admin/real-estate/image-health-dashboard");
}

export async function getAdminMediaQualityIssues(issueType, filters = {}) {
  return requestJson(
    withQuery(`/api/admin/real-estate/image-health-dashboard/issues/${encodeURIComponent(issueType)}`, filters)
  );
}

export async function recheckAdminMediaItem(mediaId) {
  return requestJson(`/api/admin/real-estate/media/${encodeURIComponent(mediaId)}/recheck`, {
    method: "POST",
  });
}

export async function rescoreAdminMediaItem(mediaId) {
  return requestJson(`/api/admin/real-estate/media/${encodeURIComponent(mediaId)}/score-quality`, {
    method: "POST",
  });
}

export async function getAdminImageCache() {
  return requestJson("/api/admin/real-estate/image-cache");
}

export async function getAdminImageCacheDetail(sourceKey, filters = {}) {
  return requestJson(
    withQuery(`/api/admin/real-estate/image-cache/${encodeURIComponent(sourceKey)}`, filters)
  );
}

export async function cacheAdminImageHealth(body = {}) {
  return requestJson("/api/admin/real-estate/image-cache/cache", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function cacheAdminImageRecord(imageId, body = {}) {
  return requestJson(`/api/admin/real-estate/image-cache/${encodeURIComponent(imageId)}/cache`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function patchAdminImageCache(imageId, body = {}) {
  return requestJson(`/api/admin/real-estate/image-cache/${encodeURIComponent(imageId)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function recalculateSourceQuality(sourceKey, body = {}) {
  return requestJson(
    `/api/admin/real-estate/source-quality/${encodeURIComponent(sourceKey)}/recalculate`,
    {
      method: "POST",
      body: JSON.stringify(body),
    }
  );
}

export async function getPropertiesMissingCoordinates(filters = {}) {
  return requestJson(withQuery("/api/admin/properties/missing-coordinates", filters));
}

export async function getStaleProperties(filters = {}) {
  return requestJson(withQuery("/api/admin/properties/stale", filters));
}

export async function getPossibleDuplicateProperties(filters = {}) {
  return requestJson(withQuery("/api/admin/properties/possible-duplicates", filters));
}

export async function previewPropertyCsvImport(file) {
  const formData = new FormData();
  formData.append("file", file);
  return requestFormData("/api/admin/property-imports/preview", formData);
}

export async function confirmPropertyCsvImport(file) {
  const formData = new FormData();
  formData.append("file", file);
  return requestFormData("/api/admin/property-imports/confirm", formData);
}

export async function getPropertyImportTemplate() {
  return requestText("/api/admin/property-imports/template");
}

export function getPropertyImportTemplateUrl() {
  return `${API_BASE_URL}/api/admin/property-imports/template`;
}

export async function getGeocodeQueue(filters = {}) {
  return requestJson(withQuery("/api/admin/real-estate/geocode-queue", filters));
}

export async function getGeocodeQueueSummary() {
  return requestJson("/api/admin/real-estate/geocode-queue/summary");
}

export async function getAdminLocationQuality(filters = {}) {
  return requestJson(withQuery("/api/admin/real-estate/location-quality", filters));
}

export async function skipGeocodeQueueEntry(queueId, body = {}) {
  return requestJson(`/api/admin/real-estate/geocode-queue/${encodeURIComponent(queueId)}/skip`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function retryGeocodeQueueEntry(queueId) {
  return requestJson(`/api/admin/real-estate/geocode-queue/${encodeURIComponent(queueId)}/retry`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function applyGeocodeQueueEntry(queueId, body) {
  return requestJson(`/api/admin/real-estate/geocode-queue/${encodeURIComponent(queueId)}/apply`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getCanonicalRealEstateDetail(id, options = {}) {
  return requestJson(`/api/real-estate/canonical-listings/${encodeURIComponent(id)}`, options);
}

export async function getPropertyDetail(id, options = {}) {
  return requestJson(`/api/real-estate/properties/${encodeURIComponent(id)}`, options);
}

export async function getCanonicalMatchCandidates(filters = {}) {
  return requestJson(withQuery("/api/admin/real-estate/duplicate-candidates", filters));
}

export async function resolveCanonicalMatchCandidate(candidateId, body) {
  return requestJson(`/api/admin/real-estate/duplicate-candidates/${encodeURIComponent(candidateId)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function getBrokerDuplicates(filters = {}) {
  return requestJson(withQuery("/api/admin/real-estate/duplicates", filters));
}

export async function getBrokerDuplicatesSummary() {
  return requestJson("/api/admin/real-estate/duplicates/summary");
}

export async function updateBrokerDuplicateStatus(candidateId, body) {
  return requestJson(
    `/api/admin/real-estate/duplicates/${encodeURIComponent(candidateId)}/status`,
    {
      method: "POST",
      body: JSON.stringify(body),
    }
  );
}

export async function getAdminCanonicalListings(filters = {}) {
  return requestJson(withQuery("/api/admin/real-estate/canonical-listings", filters));
}

export async function getAdminCanonicalListing(id) {
  return requestJson(`/api/admin/real-estate/canonical-listings/${encodeURIComponent(id)}`);
}

export async function getAdminCanonicalPropertyMedia(canonicalPropertyId) {
  return requestJson(
    `/api/admin/real-estate/canonical-properties/${encodeURIComponent(canonicalPropertyId)}/media`
  );
}

export async function recalculateAdminCanonicalPropertyMedia(canonicalPropertyId, body = {}) {
  return requestJson(
    `/api/admin/real-estate/canonical-properties/${encodeURIComponent(canonicalPropertyId)}/media/recalculate`,
    {
      method: "POST",
      body: JSON.stringify(body),
    }
  );
}

export async function getAdminCanonicalPropertyHistory(canonicalPropertyId, filters = {}) {
  return requestJson(
    withQuery(
      `/api/admin/real-estate/canonical-properties/${encodeURIComponent(canonicalPropertyId)}/history`,
      filters
    )
  );
}

export async function getAdminSourceListingHistory(sourceListingId, filters = {}) {
  return requestJson(
    withQuery(
      `/api/admin/real-estate/source-listings/${encodeURIComponent(sourceListingId)}/history`,
      filters
    )
  );
}

export async function getAdminRealEstateHistory(filters = {}) {
  return requestJson(withQuery("/api/admin/real-estate/history", filters));
}

export async function patchAdminCanonicalGalleryImage(galleryImageId, body = {}) {
  return requestJson(
    `/api/admin/real-estate/canonical-property-images/${encodeURIComponent(galleryImageId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    }
  );
}

export async function rebuildAdminCanonicalListing(id) {
  return requestJson(`/api/admin/real-estate/canonical-listings/${encodeURIComponent(id)}/rebuild`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function patchAdminCanonicalMailingPublish(id, publicStreetApproved) {
  return requestJson(
    `/api/admin/real-estate/canonical-listings/${encodeURIComponent(id)}/mailing-publish`,
    {
      method: "POST",
      body: JSON.stringify({ publicStreetApproved: Boolean(publicStreetApproved) }),
    }
  );
}

export async function updateAdminCanonicalStatus(id, body) {
  return requestJson(`/api/admin/real-estate/canonical-listings/${encodeURIComponent(id)}/status`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function unlinkAdminCanonicalBroker(id, brokerListingId) {
  return requestJson(`/api/admin/real-estate/canonical-listings/${encodeURIComponent(id)}/unlink`, {
    method: "POST",
    body: JSON.stringify({ brokerListingId }),
  });
}

export async function postCommunityReport(body) {
  return requestJson("/api/community-reports", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function submitCommunityReport(body) {
  return postCommunityReport(body);
}

export async function getPendingCommunityReports() {
  return requestJson("/api/admin/community-reports/pending");
}

export async function getAdminCommunityReports(params = {}) {
  const search = new URLSearchParams();

  if (params.moderationStatus) {
    search.set("moderationStatus", params.moderationStatus);
  }

  if (params.areaId) {
    search.set("areaId", params.areaId);
  }

  if (params.limit) {
    search.set("limit", String(params.limit));
  }

  const query = search.toString();
  return requestJson(`/api/admin/community-reports${query ? `?${query}` : ""}`);
}

export async function patchCommunityReportModeration(reportId, body) {
  return requestJson(`/api/admin/community-reports/${encodeURIComponent(reportId)}/moderation`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function moderateCommunityReport(reportId, body) {
  return patchCommunityReportModeration(reportId, body);
}
